import React, { FC, useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";

import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import ActionMenu from "@/components/ActionMenu";
import GenericAlertModal from "@/components/AlertModal";

import useDebounce from "@/hooks/useDebounce";
import { usePageStore } from "@/hooks/usePageStore";
import { useUserStore } from "@/hooks/useUserStore";
import {
  useAuditLogFirstRender,
  useCreateAuditlog,
} from "@/hooks/useAuditLogs";
import {
  useArchiveEducationalResource,
  useCreateEducationalResource,
  useEducationalResources,
  useUpdateEducationalResource,
} from "@/hooks/useEducationalResources";
import { useCategoryByType } from "@/hooks/useCategories";

import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useAppTheme } from "@/utils/theme";
import { toTitleCase } from "@/utils/toTitleCase";

import {
  EducationalResource,
  EducationalResourcePayload,
} from "@/domain/educationalResources.interface";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";

import EducationalResourceFilters, {
  Filters,
} from "@/ui/content/educationalResources/Filters";
import EducationalResourceModal from "@/ui/modals/educationalResources/EducationalResourceModal";

const EDUCATIONAL_RESOURCE_CATEGORY_TYPE = "EDUCATIONAL_RESOURCE";

const DEFAULT_FILTERS: Filters = {
  sortField: "createdAt",
  sortOrder: -1,
  pageNumber: 0,
  pageSize: 10,
  search: "",
  status: "",
  type: "",
  language: "",
};

const pickCategoryLabel = (
  langs: { name: string; language: string }[] | undefined
) => {
  if (!langs || langs.length === 0) return "";
  const en = langs.find((l) => l.language?.toLowerCase().startsWith("en"));
  return (en || langs[0]).name;
};

const pickResourceTitle = (resource: EducationalResource) => {
  const langs = resource.languages || [];
  if (langs.length === 0) return "-";
  const en = langs.find((l) => l.language?.toLowerCase().startsWith("en"));
  return (en || langs[0]).title || "-";
};

const EducationalResourcesPage: FC = () => {
  const theme = useAppTheme();
  const { showSnackBar } = useFeedBack();
  const { userInfo } = useUserStore();
  const { setPageTitle } = usePageStore();

  const filtersState = useState<Filters>(DEFAULT_FILTERS);
  const [filters, setFilters] = filtersState;
  const debouncedSearch = useDebounce({ value: filters.search });

  const [modalState, setModalState] = useState<{
    open: boolean;
    educationalResource?: EducationalResource;
  }>({ open: false });

  const [deleteAlert, setDeleteAlert] = useState<{
    open: boolean;
    educationalResource?: EducationalResource;
  }>({ open: false });

  const {
    educationalResources,
    isLoadingEducationalResources,
    isFetchingEducationalResources,
    refetchEducationalResources,
  } = useEducationalResources({
    sortField: filters.sortField,
    sortOrder: filters.sortOrder,
    pageNumber: filters.pageNumber,
    pageSize: filters.pageSize,
    search: debouncedSearch,
    status: filters.status,
    type: filters.type,
    categoryId: "",
    language: filters.language,
  });

  const createResource = useCreateEducationalResource();
  const updateResource = useUpdateEducationalResource();
  const archiveResource = useArchiveEducationalResource();
  const createAuditLog = useCreateAuditlog();

  const { category } = useCategoryByType({
    type: EDUCATIONAL_RESOURCE_CATEGORY_TYPE,
    status: "ACTIVE",
  });

  const categoryNameById = useMemo(() => {
    const map = new Map<string, string>();
    (category?.categories || []).forEach((c) => {
      if (c._id) map.set(c._id, pickCategoryLabel(c.languages) || c._id);
    });
    return map;
  }, [category]);

  const isSaving = createResource.isPending || updateResource.isPending;

  useEffect(() => {
    setPageTitle("Educational Resources");
  }, []);

  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.EDUCATIONAL_RESOURCES,
    option: OPTIONS.EDUCATIONAL_RESOURCES_LIST,
    actionCode: ACTION_CODE.WEB_EDU_RES_LIST_VIEW,
    action: "When entering",
    detail: "Viewed the list of educational resources",
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    programId: userInfo?.programId as string,
    orgUnitId: userInfo?.orgUnitIds?.[0] as string,
  });

  useEffect(() => {
    refetchEducationalResources();
  }, [
    debouncedSearch,
    filters.status,
    filters.type,
    filters.language,
    filters.sortField,
    filters.sortOrder,
    filters.pageNumber,
    filters.pageSize,
  ]);

  useEffect(() => {
    if (debouncedSearch !== "") {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.EDUCATIONAL_RESOURCES,
        option: OPTIONS.EDUCATIONAL_RESOURCES_LIST,
        actionCode: ACTION_CODE.WEB_EDU_RES_LIST_SEARCH,
        action: "When searching",
        detail: "Searched within the list of educational resources",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });
    }
  }, [debouncedSearch]);

  const handleOpenCreate = () => {
    setModalState({ open: true, educationalResource: undefined });
  };

  const handleOpenEdit = (educationalResource: EducationalResource) => {
    setModalState({ open: true, educationalResource });
  };

  const handleCloseModal = () => {
    setModalState({ open: false, educationalResource: undefined });
  };

  const handleSave = async (data: EducationalResourcePayload) => {
    const editing = modalState.educationalResource;
    const titleForLog = data.languages?.[0]?.title || "(no title)";

    try {
      if (editing) {
        await updateResource.mutateAsync({ id: editing._id, payload: data });

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.EDUCATIONAL_RESOURCES,
          option: OPTIONS.EDUCATIONAL_RESOURCE_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_EDU_RES_EDIT,
          action: "When editing an educational resource",
          detail: `Edited educational resource ${titleForLog}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The educational resource has been updated.", "success");
      } else {
        await createResource.mutateAsync(data);

        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.EDUCATIONAL_RESOURCES,
          option: OPTIONS.EDUCATIONAL_RESOURCE_ADD_EDIT,
          actionCode: ACTION_CODE.WEB_EDU_RES_ADD,
          action: "When creating an educational resource",
          detail: `Created educational resource ${titleForLog}`,
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
        });

        showSnackBar("The educational resource has been created.", "success");
      }

      handleCloseModal();
      refetchEducationalResources();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteAlert.educationalResource;
    if (!target) return;

    try {
      await archiveResource.mutateAsync(target._id);

      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.EDUCATIONAL_RESOURCES,
        option: OPTIONS.DELETE,
        actionCode: ACTION_CODE.WEB_EDU_RES_DELETE,
        action: "When deleting an educational resource",
        detail: `Deleted educational resource ${pickResourceTitle(target)}`,
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
      });

      showSnackBar("The registry has been deleted.", "success");
      setDeleteAlert({ open: false });
      refetchEducationalResources();
    } catch (error: unknown) {
      const err = error as ErrorResponse;
      showSnackBar(
        (Array.isArray(err.message) ? err.message[0] : err.message) ||
          err.error ||
          "An error occurred"
      );
    }
  };

  const headCells: HeadCell[] = [
    {
      id: "title",
      isSortable: false,
      disablePadding: true,
      label: "Title",
      align: "left",
      width: "260px",
    },
    {
      id: "category",
      isSortable: false,
      disablePadding: false,
      label: "Category",
      align: "left",
      width: "180px",
    },
    {
      id: "type",
      isSortable: true,
      disablePadding: false,
      label: "Type",
      align: "left",
      width: "120px",
    },
    {
      id: "languages",
      isSortable: false,
      disablePadding: false,
      label: "Languages",
      align: "left",
      width: "200px",
    },
    {
      id: "status",
      isSortable: true,
      disablePadding: false,
      label: "Status",
      align: "left",
      width: "120px",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: " ",
      align: "right",
      width: "80px",
    },
  ];

  const rows = useMemo(() => {
    if (!educationalResources || educationalResources.list.length === 0)
      return [];

    return educationalResources.list.map((row, index) => {
      const actionMenu = [
        {
          value: "Edit",
          onClick: () => handleOpenEdit(row),
          icon: <EditIcon fontSize="small" />,
        },
        {
          value: "Delete",
          onClick: () =>
            setDeleteAlert({ open: true, educationalResource: row }),
          icon: <DeleteOutlineIcon fontSize="small" />,
          sx: {
            color: theme.palette.error.main,
          },
        },
      ];

      const languageCodes = (row.languages || [])
        .map((l) => l.language)
        .filter(Boolean);

      return createDataRow(index, {
        title: <div>{pickResourceTitle(row)}</div>,
        category: (
          <div>
            {categoryNameById.get(row.categoryId) || row.categoryId || "-"}
          </div>
        ),
        type: <div>{row.type || "-"}</div>,
        languages:
          languageCodes.length > 0 ? (
            <Stack
              direction="row"
              spacing={0.5}
              useFlexGap
              flexWrap="wrap"
              sx={{ rowGap: 0.5 }}
            >
              {languageCodes.map((code, i) => (
                <Chip
                  key={`${row._id}-lang-${i}`}
                  size="small"
                  label={<div className="text-xs">{code}</div>}
                />
              ))}
            </Stack>
          ) : (
            <div>-</div>
          ),
        status: row.status ? (
          <Chip
            sx={{
              bgcolor:
                row.status === "ACTIVE"
                  ? theme.palette.brand.light
                  : theme.palette.warning.light,
              borderWidth: 1,
              borderStyle: "solid",
              borderColor:
                row.status === "ACTIVE"
                  ? theme.palette.brand.main
                  : theme.palette.warning.dark,
            }}
            label={<div className="text-xs">{toTitleCase(row.status)}</div>}
          />
        ) : (
          <div>-</div>
        ),
        action: <ActionMenu id={row._id} options={actionMenu} />,
      });
    });
  }, [educationalResources, theme, categoryNameById]);

  return (
    <>
      <div className="w-full overflow-y-auto scroll-smooth">
        <div className="full-width-container mx-auto">
          <div className="flex flex-col items-center justify-start min-h-screen space-y-5">
            <Card sx={{ width: "100%", borderRadius: "8px" }}>
              <CardContent
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  rowGap: "20px",
                }}
              >
                <Box
                  sx={{ width: "100%" }}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="headlineSmall" component="h1">
                    Educational Resources
                  </Typography>

                  <Button
                    size="medium"
                    onClick={handleOpenCreate}
                    variant="contained"
                    sx={{
                      borderRadius: "8px",
                      textTransform: "none",
                      textAlign: "center",
                      height: "40px",
                      backgroundColor: theme.palette.primary.dark,
                      color: theme.palette.textMain.light,
                    }}
                    startIcon={<AddIcon />}
                  >
                    ADD
                  </Button>
                </Box>

                <EducationalResourceFilters filtersState={filtersState} />

                <DataTable
                  data={rows}
                  headCells={headCells}
                  order={filters.sortOrder === 1 ? "asc" : "desc"}
                  orderBy={filters.sortField}
                  rowsPerPage={filters.pageSize}
                  page={filters.pageNumber}
                  total={educationalResources?.totalCount}
                  isLoading={
                    isLoadingEducationalResources ||
                    isFetchingEducationalResources
                  }
                  messageEmptyData="No educational resources to show."
                  onChangePage={(_event, newPage) => {
                    setFilters((prev) => ({ ...prev, pageNumber: newPage }));
                  }}
                  onChangeSize={(event) => {
                    setFilters((prev) => ({
                      ...prev,
                      pageNumber: 0,
                      pageSize: Number(event.target.value),
                    }));
                  }}
                  onChangeSort={(order, orderBy) => {
                    setFilters((prev) => {
                      const newSortField = orderBy as string;
                      const newSortOrder = order === "asc" ? 1 : -1;
                      if (
                        prev.sortField === newSortField &&
                        prev.sortOrder === newSortOrder
                      ) {
                        return prev;
                      }

                      return {
                        ...prev,
                        pageNumber: 0,
                        sortField: newSortField,
                        sortOrder: newSortOrder,
                      };
                    });
                  }}
                />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {modalState.open && (
        <EducationalResourceModal
          open={modalState.open}
          onClose={handleCloseModal}
          educationalResource={modalState.educationalResource}
          onSave={handleSave}
          isPending={isSaving}
        />
      )}

      {deleteAlert.open && (
        <GenericAlertModal
          onCancel={() => setDeleteAlert({ open: false })}
          onClick={handleConfirmDelete}
          title="Delete"
          description="Are you sure you want to delete this registry?"
          submitText="Confirm"
          cancelText="Cancel"
          isLoading={archiveResource.isPending}
          theme={theme}
        />
      )}
    </>
  );
};

export default EducationalResourcesPage;
